import axiosInstance from 'Services/APIService';

export type SubscriptionType = 'incomeUnits' | 'growthUnits';
export type RequestAction = 'accepted' | 'rejected';
export type JoinRequestAction = 'approved' | 'rejected';

export interface GroupContext {
  groupId: string;
  serviceId: string;
}

export interface GroupMemberRecord {
  memberEmail: string;
  memberName: string;
  memberAvatar?: string;
  order: number;
  orderChecked?: boolean;
  investedAmount?: string;
  contributionAmount?: number;
  amountInvestedTillDate?: number;
  contribution?: number;
  pendingContributionAmount?: number | string;
  paymentStatus?: string;
  vivuyeStatus?: string;
  adminConfirmed?: string;
  adminStatus?: string;
  amount?: number;
  paidAmount?: number;
  expenseId?: string;
  transactionDate?: string;
  isApproved?: boolean;
  version?: number;
  lastUpdatedDateTime?: string;
}

export interface GroupJoinRequestRecord {
  requestId: string;
  groupId: string;
  serviceId: string;
  memberEmail: string;
  memberName: string;
  memberAvatar?: string;
  requestMessage?: string;
  status: 'pending' | 'approved' | 'rejected';
  createdAt?: string;
  updatedAt?: string;
  actionBy?: string | null;
  actionAt?: string | null;
  actionNote?: string | null;
}

export interface BuyRequestRecord {
  requestId: string;
  requestNo?: number;
  investorsEmailId: string;
  investorName: string;
  investorAvatar?: string;
  purchaseUnits: number;
  totalInvestmentAmt: number;
  subscriptionType: SubscriptionType;
  status?: string;
  date_str?: string;
  fromMarketplace?: boolean;
  version?: number;
  lastUpdatedDateTime?: string;
}

export interface AddMemberPayload extends GroupContext {
  memberEmail: string;
  memberName: string;
  memberAvatar?: string;
}

export interface ReorderMemberPayload extends GroupContext {
  orderUpdates: Array<{
    memberEmail: string;
    order: number;
  }>;
}

export interface RemoveMemberPayload extends GroupContext {
  memberEmail: string;
}

export interface ToggleOrderCheckedPayload extends GroupContext {
  memberEmail: string;
  orderChecked: boolean;
}

export interface BuyRequestFilters {
  investorEmailId?: string;
  fromDate?: string;
  toDate?: string;
  status?: string;
}

export interface FetchBuyRequestsPayload extends GroupContext {
  unitType: SubscriptionType;
  filters?: BuyRequestFilters;
  pageSize?: number;
  continuationToken?: string | null;
}

export interface MemberSuggestion {
  memberEmail: string;
  memberName: string;
  memberAvatar?: string;
}

export interface BuyRequestActionPayload extends GroupContext {
  unitType: SubscriptionType;
  requestId: string;
  date_str: string;
  action: RequestAction;
  fromMarketplace?: boolean;
  expectedRequestVersion?: number;
  expectedLastUpdatedDateTime?: string;
}

export interface RaiseGroupJoinRequestPayload {
  groupId: string;
  serviceId?: string;
  requestMessage?: string;
  message?: string;
}

export interface GroupJoinRequestActionPayload extends GroupContext {
  requestId: string;
  action: JoinRequestAction;
  actionNote?: string;
}

export interface LedgerApprovePayload extends GroupContext {
  unitType: SubscriptionType;
  transactionDate: string;
  expenseId: string;
  isApproved: boolean;
}

export interface CreateBuyRequestPayload extends GroupContext {
  investorsEmailId: string;
  investorName: string;
  purchaseUnits: number;
  totalInvestmentAmt: number;
  subscriptionType: SubscriptionType;
  investorAvatar?: string;
  unitPrice?: number;
  operationCharge?: number;
  requestId?: string;
  date_str?: string;
  fromMarketplace?: boolean;
}

export interface GroupMembersListResponse {
  groupMembers?: GroupMemberRecord[];
}

export interface GroupJoinRequestsResponse {
  joinRequests?: GroupJoinRequestRecord[];
  totalCount?: number;
}

export type JoinState =
  | 'already_member'
  | 'pending_request'
  | 'rejected_request'
  | 'approved_request'
  | 'can_join';

export interface UserGroupJoinState {
  groupId: string;
  label: string;
  isMember: boolean;
  requestStatus: 'pending' | 'approved' | 'rejected' | null;
  joinState: JoinState;
  canRaiseJoinRequest: boolean;
  latestJoinRequest?: GroupJoinRequestRecord | null;
  serviceId?: string;
}

export interface UserGroupJoinStatesResponse {
  groups?: UserGroupJoinState[];
  userGroups?: UserGroupJoinState[];
  items?: UserGroupJoinState[];
  joinStatus?: Record<
    string,
    {
      isMember?: boolean;
      requestStatus?: 'pending' | 'approved' | 'rejected' | null;
      joinState?: JoinState;
      canRaiseJoinRequest?: boolean;
      requestId?: string | null;
    }
  >;
  serviceId?: string;
  memberEmail?: string;
  summary?: {
    totalGroups?: number;
    alreadyMemberCount?: number;
    pendingRequestCount?: number;
    canJoinCount?: number;
  };
  alreadyMemberCount?: number;
  pendingRequestCount?: number;
  canJoinCount?: number;
}

export interface UserGroupJoinStatesResult {
  groups: UserGroupJoinState[];
  summary: {
    alreadyMemberCount: number;
    pendingRequestCount: number;
    canJoinCount: number;
  };
}

export interface PublicGroupRecord {
  group_id: string;
  label: string;
  description: string;
  avatar?: string;
  peopleCount?: number;
  servicesCount?: number;
}

export interface PublicGroupsListResponse {
  groups?: PublicGroupRecord[];
  data?: PublicGroupRecord[];
  items?: PublicGroupRecord[];
}

export interface BuyRequestsListResponse {
  buyRequests?: BuyRequestRecord[];
  purchaseRequests?: BuyRequestRecord[];
  totalCount?: number;
  hasMore?: boolean;
  nextToken?: string | null;
  continuationToken?: string | null;
}

export interface MemberSuggestionsResponse {
  suggestions?: MemberSuggestion[];
}

export interface FetchBuyRequestsPageResult {
  requests: BuyRequestRecord[];
  hasMore: boolean;
  nextToken: string | null;
  totalCount: number;
}

export interface LedgerApproveResponse {
  success: boolean;
  entry?: {
    expenseId: string;
    isApproved: boolean;
    approvedBy?: string | null;
    approvedAt?: string | null;
    lastUpdatedDateTime?: string;
  };
}

export function getFundInvestmentGroupContext(): GroupContext | null {
  try {
    const selectedProject = JSON.parse(localStorage.getItem('selectedProject') || '{}') as {
      projectId?: string;
    };
    const selectedService = JSON.parse(localStorage.getItem('servicesState') || '{}') as {
      selectedServiceId?: string;
    };

    const groupId = selectedProject.projectId || '';
    const serviceId = 'fundManagementInvestment';

    if (!groupId) return null;

    return {
      groupId,
      serviceId
    };
  } catch {
    return null;
  }
}

export async function listGroupMembers(context: GroupContext) {
  const response = await axiosInstance.get<GroupMembersListResponse>(
    '/fund-investment-management/group-members/list',
    {
      params: {
        groupId: context.groupId,
        serviceId: context.serviceId
      }
    }
  );

  return response.data.groupMembers || [];
}

export async function addGroupMember(payload: AddMemberPayload) {
  const response = await axiosInstance.post('/fund-investment-management/group-members/add', payload);
  return response.data;
}

export async function reorderGroupMembers(payload: ReorderMemberPayload) {
  const response = await axiosInstance.put('/fund-investment-management/group-members/reorder', payload);
  return response.data;
}

export async function removeGroupMember(payload: RemoveMemberPayload) {
  const response = await axiosInstance.delete('/fund-investment-management/group-members/remove', {
    params: payload
  });
  return response.data;
}

export async function toggleOrderChecked(payload: ToggleOrderCheckedPayload) {
  const response = await axiosInstance.put('/fund-investment-management/group-members/toggle-check', payload);
  return response.data;
}

export async function getMemberSuggestions(
  context: GroupContext,
  options?: { search?: string; limit?: number }
) {
  const response = await axiosInstance.get<MemberSuggestionsResponse>(
    '/fund-investment-management/group-members/suggestions',
    {
      params: {
        groupId: context.groupId,
        serviceId: context.serviceId,
        search: options?.search,
        limit: options?.limit
      }
    }
  );

  return response.data.suggestions || [];
}

export async function createBuyRequest(payload: CreateBuyRequestPayload) {
  const response = await axiosInstance.post('/fund-investment-management/purchase-units', payload);
  return response.data;
}

export async function raiseGroupJoinRequest(payload: RaiseGroupJoinRequestPayload) {
  const requestMessage = payload.requestMessage ?? payload.message;
  const body = {
    groupId: payload.groupId,
    serviceId: payload.serviceId,
    requestMessage,
    message: requestMessage
  };

  const endpoints = [
    '/fund-investment-management/group-join-request/raise',
    '/api/fund-investment-management/group-join-request/raise'
  ];

  let lastError: unknown = null;

  for (const endpoint of endpoints) {
    try {
      const response = await axiosInstance.post(endpoint, body);
      return response.data;
    } catch (error: any) {
      const status = error?.response?.status;
      if (status !== 404) throw error;
      lastError = error;
    }
  }

  throw lastError || new Error('Join request API endpoint not found.');
}

export async function listGroupJoinRequests(context: GroupContext, status: string = 'pending') {
  const response = await axiosInstance.get<GroupJoinRequestsResponse>(
    '/fund-investment-management/group-join-requests',
    {
      params: {
        groupId: context.groupId,
        serviceId: context.serviceId,
        status
      }
    }
  );

  return response.data.joinRequests || [];
}

export async function listPublicGroups(): Promise<PublicGroupRecord[]> {
  const endpoints = ['/public/get-groups-list', '/api/public/get-groups-list'];

  let lastError: unknown = null;

  for (const endpoint of endpoints) {
    try {
      const response = await axiosInstance.get<PublicGroupsListResponse>(endpoint);
      return response.data.groups || response.data.data || response.data.items || [];
    } catch (error: any) {
      const status = error?.response?.status;
      if (status !== 404) throw error;
      lastError = error;
    }
  }

  throw lastError || new Error('Public groups API endpoint not found.');
}

export async function listUserGroupJoinStates(serviceId: string = 'fundManagementInvestment'): Promise<UserGroupJoinStatesResult> {
  const endpoints = [
    '/fund-investment-management/group-join-request/user-groups',
    '/api/fund-investment-management/group-join-request/user-groups'
  ];

  let response: { data: UserGroupJoinStatesResponse } | null = null;
  let lastError: unknown = null;

  for (const endpoint of endpoints) {
    try {
      response = await axiosInstance.get<UserGroupJoinStatesResponse>(endpoint, {
        params: {
          serviceId
        }
      });
      break;
    } catch (error: any) {
      const status = error?.response?.status;
      if (status !== 404) throw error;
      lastError = error;
    }
  }

  if (!response) {
    throw lastError || new Error('User groups API endpoint not found.');
  }

  const responseData = response.data;

  const directGroups = responseData.groups || responseData.userGroups || responseData.items || [];
  const mappedFromJoinStatus = responseData.joinStatus
    ? Object.entries(responseData.joinStatus).map(([groupId, value]) => ({
        groupId,
        label: groupId,
        isMember: !!value?.isMember,
        requestStatus: value?.requestStatus || null,
        joinState: value?.joinState || 'can_join',
        canRaiseJoinRequest: !!value?.canRaiseJoinRequest,
        latestJoinRequest: value?.requestId
          ? {
              requestId: value.requestId,
              groupId,
              serviceId: responseData.serviceId || serviceId,
              memberEmail: responseData.memberEmail || '',
              memberName: '',
              status: value?.requestStatus || 'pending'
            }
          : null,
        serviceId: responseData.serviceId || serviceId
      }))
    : [];

  const groups = directGroups.length > 0 ? directGroups : mappedFromJoinStatus;

  const summary = responseData.summary;

  return {
    groups,
    summary: {
      alreadyMemberCount: summary?.alreadyMemberCount ?? responseData.alreadyMemberCount ?? 0,
      pendingRequestCount: summary?.pendingRequestCount ?? responseData.pendingRequestCount ?? 0,
      canJoinCount: summary?.canJoinCount ?? responseData.canJoinCount ?? 0
    }
  };
}

export async function actionGroupJoinRequest(payload: GroupJoinRequestActionPayload) {
  const response = await axiosInstance.post('/fund-investment-management/group-join-request/action', payload);
  return response.data;
}

export async function approveLedgerEntry(payload: LedgerApprovePayload) {
  const response = await axiosInstance.post<LedgerApproveResponse>(
    '/fund-investment-management/ledger/approve',
    payload
  );

  return response.data;
}

export async function fetchBuyRequestsPage(
  payload: FetchBuyRequestsPayload
): Promise<FetchBuyRequestsPageResult> {
  const response = await axiosInstance.post<BuyRequestsListResponse>(
    '/fund-investment-management/purchase-units-requests',
    payload
  );

  return {
    requests: response.data.purchaseRequests || response.data.buyRequests || [],
    hasMore: !!response.data.hasMore,
    nextToken: response.data.nextToken || response.data.continuationToken || null,
    totalCount: response.data.totalCount || 0
  };
}

export async function fetchAllBuyRequests(payload: Omit<FetchBuyRequestsPayload, 'continuationToken'>) {
  const allRequests: BuyRequestRecord[] = [];
  let continuationToken: string | null = null;
  let hasMore = true;

  while (hasMore) {
    const page: FetchBuyRequestsPageResult = await fetchBuyRequestsPage({
      ...payload,
      continuationToken
    });
    allRequests.push(...page.requests);
    hasMore = page.hasMore;
    continuationToken = page.nextToken;

    if (!hasMore || !continuationToken) {
      break;
    }
  }

  return allRequests;
}

export async function fetchBuyRequests(payload: FetchBuyRequestsPayload) {
  const page = await fetchBuyRequestsPage(payload);
  return page.requests;
}

export async function reviewBuyRequest(payload: BuyRequestActionPayload) {
  const response = await axiosInstance.post(
    '/fund-investment-management/accept-reject-units-request',
    payload
  );

  return response.data;
}